import { useDroppable } from "@dnd-kit/core";
import { Archive, Check, ChevronDown, ChevronRight, Download, Pencil, Share2, Trash2, X } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatInstant, LIMITS, normalizeListKind, resolveDisplayZone, type ListKind } from "@checklist/shared";
import { Avatar } from "../components/Avatar.js";
import { ErrorBanner, fieldCls, inlineFieldCls, labelCls } from "../components/AuthShell.js";
import { ImportItemsModal } from "../components/ImportItemsModal.js";
import {
  DndContext,
  DragHandle,
  SortableList,
  SortableRow,
  arrayMove,
  checklistSubgroupContainerId,
  checklistUngroupedContainerId,
  closestCenter,
  parseChecklistContainerId,
  parseSectionSortableId,
  parseSubgroupBlockSortableId,
  sectionSortableId,
  subgroupBlockSortableId,
  useDndSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from "../components/SortableList.js";
import { Button } from "../components/ui/Button.js";
import { ItemSortButtons } from "../components/ui/ItemSortButtons.js";
import { CopyItemsButton } from "../components/ui/CopyItemsButton.js";
import { RemoveRowButton } from "../components/ui/RemoveRowButton.js";
import { DescriptionHint } from "../components/ui/DescriptionHint.js";
import { ProgressBar } from "../components/ui/ProgressBar.js";
import { SearchInput } from "../components/ui/SearchInput.js";
import { HoverTapTip } from "../components/ui/HoverTapTip.js";
import { SelectionCheckbox } from "../components/ui/SelectionCheckbox.js";
import { SelectionActionBar } from "../components/checklist/SelectionActionBar.js";
import { MoveItemsModal } from "../components/checklist/MoveItemsModal.js";
import { SaveSelectionAsListModal } from "../components/checklist/SaveSelectionAsListModal.js";
import { PdfExportDialog } from "../components/PdfExportDialog.js";
import { SharingPanel } from "../components/SharingPanel.js";
import { GuestNamePrompt } from "../components/GuestNamePrompt.js";
import { Modal } from "../components/ui/Modal.js";
import { useConfirm } from "../components/ui/ConfirmDialog.js";
import { useAuth } from "../context/AuthContext.js";
import { useFontScale } from "../context/FontScaleContext.js";
import { useResourceLive } from "../hooks/useResourceLive.js";
import { useItemSelection } from "../hooks/useItemSelection.js";
import {
  collectSelected,
  flattenItems,
  idsInRange,
  moveSelected,
  removeSelected,
  setCheckedForSelected,
  type MoveDestination,
} from "../lib/checklistSelection.js";
import {
  ApiError,
  addListSectionToChecklist,
  checkChecklistItem,
  createList,
  createTemplate,
  deleteChecklist,
  setChecklistArchived,
  fetchChecklist,
  fetchChecklistMembers,
  fetchLists,
  fetchMe,
  updateChecklist,
  resolveStoredShareRole,
  type ApiChecklist,
  type ApiChecklistItem,
  type ApiChecklistSection,
  type ApiChecklistSubgroup,
  type ApiListSummary,
  type ApiMember,
  type MeProfile,
} from "../lib/api.js";
import { isChecklistArchived } from "../lib/checklistArchive.js";
import { reinsertItem } from "../lib/checklistUndo.js";
import { showErrorToast, showSuccessToast, showToast, showToastWithAction } from "../components/ui/Toast.js";
import { DraftRestoreBar } from "../components/DraftRestoreBar.js";
import {
  GuardedLink,
  useDraftMirror,
  useRegisterUnsaved,
} from "../components/UnsavedChangesProvider.js";
import { clearDraft, draftKey, loadDraft } from "../lib/draftStorage.js";
import { diffCount } from "../lib/unsavedChanges.js";
import { humanizeApiError } from "../lib/apiErrors.js";
import { importResultMessage, selectImportItems } from "../lib/importParser.js";
import { checklistToPdfDoc } from "../lib/pdf/adapters.js";
import { getGuestIdentity, setGuestName } from "../lib/shareSession.js";
import { sortItemsByText, type SortDir } from "../lib/sortItems.js";
import { scaledIconPx } from "../lib/iconScale.js";
import { formatPageTitle } from "../lib/pageTitle.js";
import { ResourceNotFound } from "../components/ResourceNotFound.js";

type FilterMode = "all" | "remaining" | "done";

type CollapseState = {
  sections: Record<number, boolean>;
  subgroups: Record<string, boolean>;
};

type AddTarget = { sectionIdx: number; subgroupId: string | null };

function kindToggleCls(active: boolean): string {
  return [
    "h-9 rounded-lg border px-3 text-sm font-semibold transition",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-white text-ink hover:bg-slate-50",
  ].join(" ");
}

function filterChipCls(active: boolean): string {
  return [
    "rounded-lg border px-2.5 py-1 text-xs font-semibold transition",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-white text-ink hover:bg-slate-50",
  ].join(" ");
}

function collapseStorageKey(checklistId: string): string {
  return `checklist-collapse:${checklistId}`;
}

function loadCollapse(checklistId: string): CollapseState {
  try {
    const raw = localStorage.getItem(collapseStorageKey(checklistId));
    if (!raw) return { sections: {}, subgroups: {} };
    const parsed = JSON.parse(raw) as CollapseState;
    return {
      sections: parsed.sections ?? {},
      subgroups: parsed.subgroups ?? {},
    };
  } catch {
    return { sections: {}, subgroups: {} };
  }
}

function saveCollapse(checklistId: string, state: CollapseState) {
  try {
    localStorage.setItem(collapseStorageKey(checklistId), JSON.stringify(state));
  } catch {
    // Quota / private mode — ignore.
  }
}

function sectionProgress(section: ApiChecklistSection): { checked: number; total: number } {
  let checked = 0;
  let total = 0;
  for (const item of section.items) {
    total += 1;
    if (item.checked) checked += 1;
  }
  for (const sg of section.subgroups ?? []) {
    for (const item of sg.items) {
      total += 1;
      if (item.checked) checked += 1;
    }
  }
  return { checked, total };
}

function subgroupProgress(sg: ApiChecklistSubgroup): { checked: number; total: number } {
  let checked = 0;
  for (const item of sg.items) {
    if (item.checked) checked += 1;
  }
  return { checked, total: sg.items.length };
}

function SectionProgressMeter({
  checked,
  total,
  label,
}: {
  checked: number;
  total: number;
  label: string;
}) {
  if (total === 0) return null;
  return (
    <ProgressBar
      checked={checked}
      total={total}
      size="sm"
      className="mt-2"
      aria-label={`${label}: ${checked} of ${total} complete`}
    />
  );
}

function SubgroupProgressMeter({
  checked,
  total,
  label,
}: {
  checked: number;
  total: number;
  label: string;
}) {
  if (total === 0) return null;
  return (
    <ProgressBar
      checked={checked}
      total={total}
      size="sm"
      className="w-16 sm:w-20"
      aria-label={`${label}: ${checked} of ${total} complete`}
    />
  );
}

function countProgress(chk: ApiChecklist): { checked: number; total: number } {
  let checked = 0;
  let total = 0;
  for (const section of chk.sections) {
    const p = sectionProgress(section);
    checked += p.checked;
    total += p.total;
  }
  return { checked, total };
}

function totalItems(chk: ApiChecklist): number {
  return countProgress(chk).total;
}

function allItemTexts(chk: ApiChecklist): string[] {
  const texts: string[] = [];
  for (const section of chk.sections) {
    for (const item of section.items) texts.push(item.text);
    for (const sg of section.subgroups ?? []) {
      for (const item of sg.items) texts.push(item.text);
    }
  }
  return texts;
}

function itemMatchesFilter(
  item: ApiChecklistItem,
  query: string,
  filter: FilterMode
): boolean {
  if (filter === "remaining" && item.checked) return false;
  if (filter === "done" && !item.checked) return false;
  if (query && !item.text.toLowerCase().includes(query)) return false;
  return true;
}

function DroppableRegion({
  id,
  children,
  className = "",
  disabled = false,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled,
    data: { type: "container" },
  });
  return (
    <div
      ref={setNodeRef}
      className={[className, !disabled && isOver ? "ring-2 ring-brand-500/30 rounded-lg" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

function findItemLocation(
  itemId: UniqueIdentifier,
  sections: ApiChecklistSection[]
): { sectionIdx: number; subgroupId: string | null } | null {
  const sid = String(itemId);
  for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx++) {
    const section = sections[sectionIdx]!;
    if (section.items.some((it) => it.id === sid)) {
      return { sectionIdx, subgroupId: null };
    }
    for (const sg of section.subgroups ?? []) {
      if (sg.items.some((it) => it.id === sid)) {
        return { sectionIdx, subgroupId: sg.id };
      }
    }
  }
  return null;
}

function displaySectionName(name: string): string {
  return name.trim() ? name : "Untitled section";
}

function isLaterDuplicateSectionName(
  sections: { name: string }[],
  sectionIdx: number
): boolean {
  const label = displaySectionName(sections[sectionIdx]?.name ?? "");
  for (let i = 0; i < sectionIdx; i++) {
    if (displaySectionName(sections[i]?.name ?? "") === label) return true;
  }
  return false;
}

function readContainerItems(
  sections: ApiChecklistSection[],
  sectionIdx: number,
  subgroupId: string | null
): ApiChecklistItem[] {
  const section = sections[sectionIdx];
  if (!section) return [];
  if (subgroupId === null) return section.items;
  return section.subgroups?.find((sg) => sg.id === subgroupId)?.items ?? [];
}

function writeContainerItems(
  sections: ApiChecklistSection[],
  sectionIdx: number,
  subgroupId: string | null,
  nextItems: ApiChecklistItem[]
): ApiChecklistSection[] {
  return sections.map((section, i) => {
    if (i !== sectionIdx) return section;
    if (subgroupId === null) return { ...section, items: nextItems };
    return {
      ...section,
      subgroups: (section.subgroups ?? []).map((sg) =>
        sg.id === subgroupId ? { ...sg, items: nextItems } : sg
      ),
    };
  });
}

function normalizeSections(sections: ApiChecklistSection[]): ApiChecklistSection[] {
  return sections.map((s) => ({
    ...s,
    subgroups: s.subgroups ?? [],
  }));
}

export function ChecklistPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const mySub = user?.sub ?? "";
  const sensors = useDndSensors();
  const { scale: fontScale } = useFontScale();
  const checkboxPx = scaledIconPx(20, fontScale); // matches base h-5 w-5
  const chevronPx = scaledIconPx(16, fontScale); // matches base h-4 w-4
  const pencilPx = scaledIconPx(14, fontScale); // matches base h-3.5 w-3.5

  const [checklist, setChecklist] = useState<ApiChecklist | null>(null);
  // Async toast actions (item undo) outlive the render that created them.
  const checklistRef = useRef<ApiChecklist | null>(null);
  checklistRef.current = checklist;
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (notFound) return;
    document.title = formatPageTitle(checklist?.name || "Checklist");
  }, [checklist?.name, notFound]);
  useEffect(() => {
    if (!notFound) return;
    document.title = formatPageTitle("Checklist not found");
  }, [notFound]);
  const [me, setMe] = useState<MeProfile | null>(null);
  const [members, setMembers] = useState<ApiMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemText, setEditingItemText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [addTarget, setAddTarget] = useState<AddTarget | null>(null);
  const [newItemText, setNewItemText] = useState("");
  const [adding, setAdding] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [liveNames, setLiveNames] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [collapse, setCollapse] = useState<CollapseState>({ sections: {}, subgroups: {} });
  const [newSectionName, setNewSectionName] = useState("");
  const [newSubgroupName, setNewSubgroupName] = useState<Record<number, string>>({});
  const [addingSection, setAddingSection] = useState(false);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerFilter, setListPickerFilter] = useState("");
  const [lists, setLists] = useState<ApiListSummary[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [addingListId, setAddingListId] = useState<string | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savedTemplate, setSavedTemplate] = useState<{ id: string; name: string } | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  const [shareRole, setShareRole] = useState<"read" | "write" | null>(null);
  const [guestPromptOpen, setGuestPromptOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<AddTarget & { item: ApiChecklistItem } | null>(null);
  const [description, setDescription] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  // Import modal state must live with the other hooks (above every early
  // return, including `if (loading)` ~1150). This file has no react-hooks
  // lint; a hook added below that return typechecks and crashes at runtime.
  const [importOpen, setImportOpen] = useState(false);
  const [importTarget, setImportTarget] = useState<AddTarget | null>(null);
  const [pageMode, setPageMode] = useState<"use" | "edit">("use");
  const selection = useItemSelection();
  const [moveOpen, setMoveOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [savedList, setSavedList] = useState<{ id: string; name: string } | null>(null);
  const prevPctRef = useRef<number | null>(null);
  // True once the user edits description/tags without saving; guards silent
  // focus refetches from clobbering an in-progress edit (those fields have
  // no per-keystroke persist, unlike items/kind).
  const detailsDirtyRef = useRef(false);
  // Unsaved-edit protection for the description / tags panel (the only checklist
  // fields without per-action persistence). Items and structure already save on
  // every change, so they need no draft mirror.
  const [pendingDetails, setPendingDetails] = useState<
    { description: string; tagsText: string } | null
  >(null);
  const detailsMirrorKey = draftKey("checklist", id ?? "unknown");
  const savedDetails = useMemo(
    () => ({
      description: checklist?.description ?? "",
      tagsText: (checklist?.tags ?? []).join(", "),
    }),
    [checklist?.description, checklist?.tags]
  );
  const draftDetails = useMemo(
    () => ({ description, tagsText }),
    [description, tagsText]
  );
  const detailsUnsavedCount = checklist
    ? diffCount(
        savedDetails as unknown as Record<string, unknown>,
        draftDetails as unknown as Record<string, unknown>
      )
    : 0;
  useRegisterUnsaved(`checklist:${id ?? "unknown"}`, detailsUnsavedCount);
  const detailsDraftMirror = useDraftMirror(
    detailsMirrorKey,
    draftDetails,
    detailsUnsavedCount > 0 && !!checklist
  );

  const detailsRestoreCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!checklist) return;
    if (detailsRestoreCheckedRef.current === detailsMirrorKey) return;
    detailsRestoreCheckedRef.current = detailsMirrorKey;
    const stored = loadDraft(detailsMirrorKey);
    if (
      !stored ||
      typeof stored !== "object" ||
      !("description" in stored) ||
      !("tagsText" in stored)
    ) {
      return;
    }
    if (
      diffCount(
        savedDetails as unknown as Record<string, unknown>,
        stored as unknown as Record<string, unknown>
      ) === 0
    ) {
      clearDraft(detailsMirrorKey);
      return;
    }
    setPendingDetails(stored as { description: string; tagsText: string });
  }, [checklist, detailsMirrorKey, savedDetails]);

  async function loadChecklist(opts?: { silent?: boolean }) {
    if (!id) return;
    if (!opts?.silent) {
      setLoading(true);
      setNotFound(false);
    }
    try {
      const data = await fetchChecklist(id);
      setChecklist({
        ...data,
        kind: normalizeListKind(data.kind),
        sections: normalizeSections(data.sections),
      });
      if (!opts?.silent || !detailsDirtyRef.current) {
        setDescription(data.description ?? "");
        setTagsText((data.tags ?? []).join(", "));
        detailsDirtyRef.current = false;
      }
      const role = await resolveStoredShareRole("checklist", id);
      if (role) setShareRole(role);
      setNotFound(false);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      if (err instanceof ApiError && err.status === 403) {
        if (!user) {
          navigate(`/request-access/checklist/${id}`, { replace: true });
          return;
        }
        setError("You no longer have access to this checklist.");
        setChecklist(null);
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setNotFound(true);
        return;
      }
      setError(humanizeApiError(err, "Failed to load checklist"));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }

  async function loadMembers() {
    if (!id) return;
    try {
      const list = await fetchChecklistMembers(id);
      setMembers(list);
    } catch {
      // Attribution falls back without member list.
    }
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await fetchMe();
        if (!cancelled) setMe(profile);
      } catch {
        // Attribution chip falls back without profile.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void loadChecklist();
    void loadMembers();
  }, [id, user, navigate]);

  useEffect(() => {
    if (!id) return;
    setCollapse(loadCollapse(id));
  }, [id]);

  useEffect(() => {
    setPageMode("use");
  }, [id]);

  useEffect(() => {
    function onFocus() {
      void loadChecklist({ silent: true });
      void loadMembers();
    }
    function onVisibility() {
      if (document.visibilityState === "visible") onFocus();
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [id]);

  useEffect(() => {
    if (!selection.isMultiSelectMode) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") selection.exitMode();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selection.isMultiSelectMode, selection.exitMode]);

  useEffect(() => {
    prevPctRef.current = null;
    setCelebrate(false);
  }, [id]);

  useEffect(() => {
    if (!checklist) return;
    const { checked, total } = countProgress(checklist);
    const pct = total === 0 ? 0 : Math.round((checked / total) * 100);
    if (prevPctRef.current === null) {
      prevPctRef.current = pct;
      return;
    }
    if (prevPctRef.current < 100 && pct === 100 && total > 0) {
      setCelebrate(true);
      showSuccessToast("All done — nice work!");
      const t = window.setTimeout(() => setCelebrate(false), 2400);
      prevPctRef.current = pct;
      return () => window.clearTimeout(t);
    }
    prevPctRef.current = pct;
  }, [checklist]);

  function setCollapsePersisted(next: CollapseState | ((prev: CollapseState) => CollapseState)) {
    setCollapse((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      if (id) saveCollapse(id, value);
      return value;
    });
  }

  function applyItemUpdate(
    sectionIdx: number,
    itemId: string,
    updated: ApiChecklistItem,
    subgroupId: string | null
  ) {
    setChecklist((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((section, i) => {
          if (i !== sectionIdx) return section;
          if (subgroupId === null) {
            return {
              ...section,
              items: section.items.map((it) => (it.id === itemId ? updated : it)),
            };
          }
          return {
            ...section,
            subgroups: (section.subgroups ?? []).map((sg) =>
              sg.id === subgroupId
                ? {
                    ...sg,
                    items: sg.items.map((it) => (it.id === itemId ? updated : it)),
                  }
                : sg
            ),
          };
        }),
      };
    });
  }

  useResourceLive("checklist", id, {
    memberCount: checklist?.memberCount,
    activeShareCount: checklist?.activeShareCount,
  }, {
    onItem: (msg) => {
    if (msg.screenName && msg.checkedBy) {
      setLiveNames((prev) =>
        prev[msg.checkedBy!] === msg.screenName
          ? prev
          : { ...prev, [msg.checkedBy!]: msg.screenName }
      );
    }
    const subgroupId = msg.subgroupId ?? null;
    setChecklist((prev) => {
      if (!prev) return prev;
      const section = prev.sections[msg.sectionIdx];
      if (!section) return prev;

      let existing: ApiChecklistItem | undefined;
      if (subgroupId) {
        existing = section.subgroups?.find((sg) => sg.id === subgroupId)?.items.find(
          (it) => it.id === msg.itemId
        );
      } else {
        existing = section.items.find((it) => it.id === msg.itemId);
        // Backward-compatible: if omitted subgroupId, search subgroups too.
        if (!existing) {
          for (const sg of section.subgroups ?? []) {
            const hit = sg.items.find((it) => it.id === msg.itemId);
            if (hit) {
              existing = hit;
              const next: ApiChecklistItem = {
                id: msg.itemId,
                text: hit.text,
                checked: msg.checked,
              };
              if (msg.checked && msg.checkedBy) {
                next.checkedBy = msg.checkedBy;
                next.checkedAt = new Date().toISOString();
              }
              return {
                ...prev,
                sections: prev.sections.map((s, i) => {
                  if (i !== msg.sectionIdx) return s;
                  return {
                    ...s,
                    subgroups: (s.subgroups ?? []).map((g) =>
                      g.id === sg.id
                        ? {
                            ...g,
                            items: g.items.map((it) =>
                              it.id === msg.itemId ? next : it
                            ),
                          }
                        : g
                    ),
                  };
                }),
              };
            }
          }
        }
      }
      if (!existing) return prev;

      const next: ApiChecklistItem = {
        id: msg.itemId,
        text: existing.text,
        checked: msg.checked,
      };
      if (msg.checked && msg.checkedBy) {
        next.checkedBy = msg.checkedBy;
        next.checkedAt = new Date().toISOString();
      }

      if (subgroupId) {
        return {
          ...prev,
          sections: prev.sections.map((s, i) => {
            if (i !== msg.sectionIdx) return s;
            return {
              ...s,
              subgroups: (s.subgroups ?? []).map((sg) =>
                sg.id === subgroupId
                  ? {
                      ...sg,
                      items: sg.items.map((it) =>
                        it.id === msg.itemId ? next : it
                      ),
                    }
                  : sg
              ),
            };
          }),
        };
      }

      return {
        ...prev,
        sections: prev.sections.map((s, i) => {
          if (i !== msg.sectionIdx) return s;
          return {
            ...s,
            items: s.items.map((it) => (it.id === msg.itemId ? next : it)),
          };
        }),
      };
    });
    if (typeof msg.version === "number") {
      const v = msg.version;
      setChecklist((prev) => (prev && v > prev.version ? { ...prev, version: v } : prev));
    }
    },
    onResource: (message) => {
      if (!checklist || message.version <= checklist.version) return;
      if (persisting || resetting || adding || addingSection) {
        showErrorToast("Someone else changed this — reload to see their changes.");
        setError("Someone else changed this — reload to see their changes.");
        return;
      }
      void loadChecklist({ silent: true });
    },
  });

  function reportActionError(err: unknown, fallback: string) {
    showErrorToast(humanizeApiError(err, fallback));
    if (err instanceof ApiError && err.code === "STALE_VERSION") {
      setError("Someone else changed this — reload to see their changes.");
    }
  }

  async function persistSections(nextSections: ApiChecklistSection[]): Promise<boolean> {
    if (!id) return false;
    const normalized = normalizeSections(nextSections);
    setChecklist((prev) => (prev ? { ...prev, sections: normalized } : prev));
    setPersisting(true);
    setError(null);
    try {
      const updated = await updateChecklist(id, {
        version: checklistRef.current?.version,
        sections: normalized.map((section) => ({
          name: section.name,
          items: section.items.map((it) => ({
            id: it.id,
            text: it.text,
            checked: it.checked,
            checkedBy: it.checkedBy,
            checkedAt: it.checkedAt,
          })),
          subgroups: (section.subgroups ?? []).map((sg) => ({
            id: sg.id,
            name: sg.name,
            items: sg.items.map((it) => ({
              id: it.id,
              text: it.text,
              checked: it.checked,
              checkedBy: it.checkedBy,
              checkedAt: it.checkedAt,
            })),
          })),
        })),
      });
      setChecklist({
        ...updated,
        kind: normalizeListKind(updated.kind),
        sections: normalizeSections(updated.sections),
      });
      return true;
    } catch (err) {
      await loadChecklist({ silent: true });
      reportActionError(err, "Failed to save changes");
      return false;
    } finally {
      setPersisting(false);
    }
  }

  function currentAttribution(): { sub: string; screenName?: string } | null {
    if (user) return { sub: mySub, screenName: me?.screenName };
    if (shareRole === "write") {
      const guest = getGuestIdentity();
      return { sub: `guest:${guest.guestId}`, screenName: guest.displayName || undefined };
    }
    return null;
  }

  async function onBulkCheck(checked: boolean) {
    if (!checklist) return;
    const count = selection.selectedIds.size;
    setBulkBusy(true);
    try {
      const ok = await persistSections(
        setCheckedForSelected(checklist.sections, selection.selectedIds, checked, currentAttribution())
      );
      if (ok) {
        showSuccessToast(
          `${checked ? "Checked" : "Unchecked"} ${count} item${count === 1 ? "" : "s"}.`
        );
      }
    } finally {
      setBulkBusy(false);
    }
  }

  async function onBulkDelete() {
    if (!checklist) return;
    const count = selection.selectedIds.size;
    const ok = await confirm({
      title: `Delete ${count} selected item${count === 1 ? "" : "s"}?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const saved = await persistSections(removeSelected(checklist.sections, selection.selectedIds));
      if (saved) {
        selection.clearSelection();
        showSuccessToast(`Deleted ${count} item${count === 1 ? "" : "s"}.`);
      }
    } finally {
      setBulkBusy(false);
    }
  }

  async function onMoveSelected(destination: MoveDestination) {
    if (!checklist) return;
    const result = moveSelected(checklist.sections, selection.selectedIds, destination, {
      sectionsMax: LIMITS.templateSectionsMax,
      subgroupsMax: LIMITS.subgroupsMax,
    });
    if (!result.ok) {
      const message =
        result.reason === "sectionLimit"
          ? `Checklists can have at most ${LIMITS.templateSectionsMax} sections.`
          : result.reason === "subgroupLimit"
            ? `Sections can have at most ${LIMITS.subgroupsMax} subgroups.`
            : result.reason === "noop"
              ? "Those items are already there."
              : "Failed to move items.";
      showErrorToast(message);
      return;
    }
    setBulkBusy(true);
    try {
      const ok = await persistSections(result.sections);
      if (ok) {
        setMoveOpen(false);
        selection.clearSelection();
        showSuccessToast(`Moved ${result.movedCount} item${result.movedCount === 1 ? "" : "s"}.`);
      }
    } finally {
      setBulkBusy(false);
    }
  }

  async function onSaveSelectionAsList(input: {
    name: string;
    kind: ListKind;
    removeFromChecklist: boolean;
  }) {
    if (!checklist) return;
    setBulkBusy(true);
    try {
      const created = await createList({
        name: input.name,
        kind: input.kind,
        items: collectSelected(checklist.sections, selection.selectedIds).map((located) => ({
          text: located.item.text,
        })),
      });
      if (input.removeFromChecklist) {
        await persistSections(removeSelected(checklist.sections, selection.selectedIds));
      }
      setSaveOpen(false);
      selection.clearSelection();
      setSavedList({ id: created.id, name: created.name });
      showToast(`Saved as list "${created.name}"`);
    } catch (err) {
      reportActionError(err, "Failed to save list");
    } finally {
      setBulkBusy(false);
    }
  }

  async function onToggle(
    sectionIdx: number,
    item: ApiChecklistItem,
    subgroupId: string | null
  ) {
    if (!id || !checklist) return;
    if (!user && shareRole === "write" && !getGuestIdentity().displayName) {
      setPendingToggle({ sectionIdx, subgroupId, item });
      setGuestPromptOpen(true);
      return;
    }
    const nextChecked = !item.checked;
    setBusyItem(item.id);
    setError(null);
    try {
      const updated = await checkChecklistItem(id, {
        sectionIdx,
        itemId: item.id,
        checked: nextChecked,
        subgroupId,
      });
      const { checklistVersion, ...checked } = updated;
      applyItemUpdate(sectionIdx, item.id, checked, subgroupId);
      if (typeof checklistVersion === "number") {
        setChecklist((prev) =>
          prev && checklistVersion > prev.version ? { ...prev, version: checklistVersion } : prev
        );
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "CHECK_CONFLICT") {
        await loadChecklist({ silent: true });
        showErrorToast("That item changed while you were checking it. Try again.");
        return;
      }
      reportActionError(err, "Failed to update item");
    } finally {
      setBusyItem(null);
    }
  }

  async function onKindChange(nextKind: ListKind) {
    if (!id || !checklist || checklist.kind === nextKind) return;
    setError(null);
    const prevKind = checklist.kind;
    setChecklist({ ...checklist, kind: nextKind });
    try {
      const updated = await updateChecklist(id, { kind: nextKind, version: checklist.version });
      setChecklist({
        ...updated,
        kind: normalizeListKind(updated.kind),
        sections: normalizeSections(updated.sections),
      });
    } catch (err) {
      setChecklist((prev) => (prev ? { ...prev, kind: prevKind } : prev));
      reportActionError(err, "Failed to update kind");
    }
  }

  function parseTags(): string[] {
    return tagsText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  async function onSaveDetails() {
    if (!id || !checklist) return;
    setError(null);
    setSavingDetails(true);
    try {
      const updated = await updateChecklist(id, {
        description: description.trim() || undefined,
        tags: parseTags(),
        version: checklist.version,
      });
      setChecklist({
        ...updated,
        kind: normalizeListKind(updated.kind),
        sections: normalizeSections(updated.sections),
      });
      setDescription(updated.description ?? "");
      setTagsText((updated.tags ?? []).join(", "));
      detailsDirtyRef.current = false;
      detailsDraftMirror.discard();
      setPendingDetails(null);
      showSuccessToast("Details saved.");
    } catch (err) {
      reportActionError(err, "Failed to save details");
    } finally {
      setSavingDetails(false);
    }
  }

  async function onResetAll() {
    if (!id || !checklist) return;
    const ok = await confirm({
      title: "Uncheck all items?",
      message: "Attribution will be cleared.",
      confirmLabel: "Uncheck all",
    });
    if (!ok) return;
    setResetting(true);
    setError(null);
    try {
      const sections = checklist.sections.map((section) => ({
        name: section.name,
        items: section.items.map((it) => ({
          id: it.id,
          text: it.text,
          checked: false,
        })),
        subgroups: (section.subgroups ?? []).map((sg) => ({
          id: sg.id,
          name: sg.name,
          items: sg.items.map((it) => ({
            id: it.id,
            text: it.text,
            checked: false,
          })),
        })),
      }));
      const updated = await updateChecklist(id, { sections, version: checklist.version });
      setChecklist({
        ...updated,
        kind: normalizeListKind(updated.kind),
        sections: normalizeSections(updated.sections),
      });
      showSuccessToast("All items unchecked.");
    } catch (err) {
      reportActionError(err, "Failed to reset");
    } finally {
      setResetting(false);
    }
  }

  async function onAddItem() {
    if (!id || !checklist || !addTarget) return;
    const text = newItemText.trim().replace(/\s+/g, " ");
    if (!text) return;
    if (totalItems(checklist) >= LIMITS.checklistItemsMax) {
      showErrorToast(`Checklists can have at most ${LIMITS.checklistItemsMax} items.`);
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const newItem: ApiChecklistItem = {
        id: crypto.randomUUID(),
        text,
        checked: false,
      };
      const sections = writeContainerItems(
        checklist.sections,
        addTarget.sectionIdx,
        addTarget.subgroupId,
        [
          ...readContainerItems(
            checklist.sections,
            addTarget.sectionIdx,
            addTarget.subgroupId
          ),
          newItem,
        ]
      );
      const ok = await persistSections(sections);
      if (ok) {
        setNewItemText("");
        setAddTarget(null);
        showSuccessToast("Item added.");
      }
    } finally {
      setAdding(false);
    }
  }

  async function onUndoRemove(
    sectionIdx: number,
    subgroupId: string | null,
    removed: ApiChecklistItem,
    index: number
  ) {
    const current = checklistRef.current;
    const section = current?.sections[sectionIdx];
    const subgroupMissing =
      subgroupId !== null &&
      !(section?.subgroups ?? []).some((sg) => sg.id === subgroupId);
    if (!current || !section || subgroupMissing) {
      showErrorToast("Couldn't undo: that section was removed.");
      return;
    }
    const items = readContainerItems(current.sections, sectionIdx, subgroupId);
    const nextItems = reinsertItem(items, removed, index);
    if (nextItems === items) return;
    await persistSections(
      writeContainerItems(current.sections, sectionIdx, subgroupId, nextItems)
    );
  }

  async function onDeleteItem(
    sectionIdx: number,
    itemId: string,
    subgroupId: string | null
  ) {
    if (!checklist) return;
    const prevItems = readContainerItems(checklist.sections, sectionIdx, subgroupId);
    const index = prevItems.findIndex((it) => it.id === itemId);
    const removed = prevItems[index];
    if (!removed) return;
    const next = writeContainerItems(
      checklist.sections,
      sectionIdx,
      subgroupId,
      prevItems.filter((it) => it.id !== itemId)
    );
    const ok = await persistSections(next);
    if (ok) {
      showToastWithAction("Item removed.", {
        label: "Undo",
        onAction: () => void onUndoRemove(sectionIdx, subgroupId, removed, index),
      });
    }
  }

  function startItemEdit(item: ApiChecklistItem) {
    setEditingItemId(item.id);
    setEditingItemText(item.text);
  }

  function cancelItemEdit() {
    setEditingItemId(null);
    setEditingItemText("");
  }

  async function saveItemEdit(
    sectionIdx: number,
    itemId: string,
    subgroupId: string | null
  ) {
    if (!checklist) return;
    const text = editingItemText.trim().replace(/\s+/g, " ");
    if (!text) {
      showErrorToast("An item needs text.");
      return;
    }

    const items = readContainerItems(checklist.sections, sectionIdx, subgroupId);
    const existing = items.find((item) => item.id === itemId);
    if (!existing) return;
    if (existing.text === text) {
      cancelItemEdit();
      return;
    }

    const ok = await persistSections(
      writeContainerItems(
        checklist.sections,
        sectionIdx,
        subgroupId,
        items.map((item) => (item.id === itemId ? { ...item, text } : item))
      )
    );
    if (ok) {
      cancelItemEdit();
      showSuccessToast("Item updated.");
    }
  }

  async function onAddSection() {
    if (!checklist) return;
    const name = newSectionName.trim().replace(/\s+/g, " ");
    if (!name) return;
    if (checklist.sections.length >= LIMITS.templateSectionsMax) {
      showErrorToast(`Checklists can have at most ${LIMITS.templateSectionsMax} sections.`);
      return;
    }
    setAddingSection(false);
    setNewSectionName("");
    const ok = await persistSections([
      ...checklist.sections,
      { name, items: [], subgroups: [] },
    ]);
    if (ok) showSuccessToast("Section added.");
  }

  function openListPicker() {
    if (!user) return;
    setListPickerFilter("");
    setListPickerOpen(true);
    setLoadingLists(true);
    void fetchLists()
      .then(setLists)
      .catch((err) => reportActionError(err, "Failed to load lists"))
      .finally(() => setLoadingLists(false));
  }

  async function onAddListSection(list: ApiListSummary) {
    if (!id || !checklist) return;
    setAddingListId(list.id);
    try {
      const updated = await addListSectionToChecklist(id, list.id, checklist.version);
      setChecklist({
        ...updated,
        kind: normalizeListKind(updated.kind),
        sections: normalizeSections(updated.sections),
      });
      setListPickerOpen(false);
      showSuccessToast(`Added "${list.name}" as a new section.`);
    } catch (err) {
      reportActionError(err, "Failed to add list");
    } finally {
      setAddingListId(null);
    }
  }

  async function onRenameSection(sectionIdx: number, name: string) {
    if (!checklist) return;
    const cleaned = name.trim().replace(/\s+/g, " ");
    const sections = checklist.sections.map((s, i) =>
      i === sectionIdx ? { ...s, name: cleaned } : s
    );
    await persistSections(sections);
  }

  async function onDeleteSection(sectionIdx: number) {
    if (!checklist) return;
    const section = checklist.sections[sectionIdx];
    if (!section) return;
    const count = sectionProgress(section).total;
    const ok = await confirm({
      title: `Delete section "${section.name}"?`,
      message:
        count > 0
          ? `This will also delete its ${count} item${count === 1 ? "" : "s"}.`
          : "This section is empty.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await persistSections(checklist.sections.filter((_, i) => i !== sectionIdx));
  }

  async function onAddSubgroup(sectionIdx: number) {
    if (!checklist) return;
    const section = checklist.sections[sectionIdx];
    if (!section) return;
    const name = (newSubgroupName[sectionIdx] ?? "").trim().replace(/\s+/g, " ");
    if (!name) return;
    if ((section.subgroups ?? []).length >= LIMITS.subgroupsMax) {
      showErrorToast(`Sections can have at most ${LIMITS.subgroupsMax} subgroups.`);
      return;
    }
    const sections = checklist.sections.map((s, i) =>
      i === sectionIdx
        ? {
            ...s,
            subgroups: [
              ...(s.subgroups ?? []),
              { id: crypto.randomUUID(), name, items: [] },
            ],
          }
        : s
    );
    setNewSubgroupName((prev) => ({ ...prev, [sectionIdx]: "" }));
    await persistSections(sections);
  }

  async function onRenameSubgroup(
    sectionIdx: number,
    subgroupId: string,
    name: string
  ) {
    if (!checklist) return;
    const cleaned = name.trim().replace(/\s+/g, " ");
    if (!cleaned) return;
    const sections = checklist.sections.map((s, i) =>
      i === sectionIdx
        ? {
            ...s,
            subgroups: (s.subgroups ?? []).map((sg) =>
              sg.id === subgroupId ? { ...sg, name: cleaned } : sg
            ),
          }
        : s
    );
    await persistSections(sections);
  }

  async function onDeleteSubgroup(sectionIdx: number, subgroupId: string) {
    if (!checklist) return;
    const sg = checklist.sections[sectionIdx]?.subgroups?.find(
      (s) => s.id === subgroupId
    );
    if (!sg) return;
    if (sg.items.length > 0) {
      const ok = await confirm({
        title: `Delete subgroup "${sg.name}"?`,
        message: `This will also delete its ${sg.items.length} item${
          sg.items.length === 1 ? "" : "s"
        }.`,
        confirmLabel: "Delete",
        destructive: true,
      });
      if (!ok) return;
    }
    const sections = checklist.sections.map((s, i) =>
      i === sectionIdx
        ? {
            ...s,
            subgroups: (s.subgroups ?? []).filter((g) => g.id !== subgroupId),
          }
        : s
    );
    await persistSections(sections);
  }

  async function onDelete() {
    if (!id || !checklist) return;
    const ok = await confirm({
      title: `Delete "${checklist.name}"?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteChecklist(id);
      navigate("/checklists", { replace: true });
    } catch (err) {
      reportActionError(err, "Failed to delete");
      setDeleting(false);
    }
  }

  async function onToggleArchive() {
    if (!id || !checklist) return;
    const currentlyArchived = isChecklistArchived(checklist.archivedAt);
    if (!currentlyArchived) {
      const ok = await confirm({
        title: `Archive "${checklist.name}"?`,
        message:
          "It will leave My Checklists' main list and appear under Archived. Anyone with the link can still open and use it.",
        confirmLabel: "Archive",
      });
      if (!ok) return;
    }
    setArchiving(true);
    setError(null);
    try {
      const next = await setChecklistArchived(id, !currentlyArchived);
      setChecklist(next);
    } catch (err) {
      reportActionError(err, currentlyArchived ? "Failed to unarchive" : "Failed to archive");
    } finally {
      setArchiving(false);
    }
  }

  async function onSaveAsTemplate() {
    if (!checklist) return;
    const ok = await confirm({
      title: "Save as template?",
      message: "Next time, create this whole checklist in one click.",
      confirmLabel: "Save as template",
    });
    if (!ok) return;
    setSavingTemplate(true);
    setError(null);
    setSavedTemplate(null);
    try {
      const tmpl = await createTemplate({
        name: checklist.name,
        kind: checklist.kind,
        sections: checklist.sections.map((section) => ({
          type: "inline",
          name: section.name,
          items: [
            ...section.items.map((it) => ({ text: it.text })),
            ...(section.subgroups ?? []).flatMap((sg) =>
              sg.items.map((it) => ({ text: it.text }))
            ),
          ],
        })),
      });
      setSavedTemplate({ id: tmpl.id, name: tmpl.name });
      showToast(`Saved as template "${tmpl.name}"`);
    } catch (err) {
      reportActionError(err, "Failed to save as template");
    } finally {
      setSavingTemplate(false);
    }
  }

  async function onImportConfirm(preview: string[]) {
    if (!checklist || !importTarget) return;
    const existing = allItemTexts(checklist);
    const remaining = LIMITS.checklistItemsMax - totalItems(checklist);
    const { toAdd, skippedDuplicate, skippedLimit } = selectImportItems(
      preview,
      existing,
      remaining
    );
    if (toAdd.length === 0) {
      showSuccessToast(importResultMessage(0, skippedDuplicate, skippedLimit));
      return;
    }
    const newItems: ApiChecklistItem[] = toAdd.map((text) => ({
      id: crypto.randomUUID(),
      text,
      checked: false,
    }));
    const sections = writeContainerItems(
      checklist.sections,
      importTarget.sectionIdx,
      importTarget.subgroupId,
      [
        ...readContainerItems(
          checklist.sections,
          importTarget.sectionIdx,
          importTarget.subgroupId
        ),
        ...newItems,
      ]
    );
    const ok = await persistSections(sections);
    if (ok) {
      showSuccessToast(importResultMessage(toAdd.length, skippedDuplicate, skippedLimit));
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    if (!checklist) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeType = active.data.current?.type as string | undefined;
    const sections = checklist.sections;

    if (activeType === "section") {
      const oldIndex = parseSectionSortableId(String(active.id));
      const newIndex = parseSectionSortableId(String(over.id));
      if (oldIndex === null || newIndex === null || oldIndex === newIndex) return;
      void persistSections(arrayMove(sections, oldIndex, newIndex));
      return;
    }

    if (activeType === "subgroup") {
      const activeLoc = parseSubgroupBlockSortableId(String(active.id));
      const overLoc =
        parseSubgroupBlockSortableId(String(over.id)) ??
        (over.data.current?.type === "subgroup"
          ? parseSubgroupBlockSortableId(String(over.id))
          : null);
      if (!activeLoc || !overLoc) return;
      if (activeLoc.sectionIdx !== overLoc.sectionIdx) return;
      const section = sections[activeLoc.sectionIdx];
      if (!section) return;
      const sgs = [...(section.subgroups ?? [])];
      const oldIndex = sgs.findIndex((sg) => sg.id === activeLoc.subgroupId);
      const newIndex = sgs.findIndex((sg) => sg.id === overLoc.subgroupId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const next = sections.map((s, i) =>
        i === activeLoc.sectionIdx
          ? { ...s, subgroups: arrayMove(sgs, oldIndex, newIndex) }
          : s
      );
      void persistSections(next);
      return;
    }

    if (activeType !== "item") return;

    const activeLoc = findItemLocation(active.id, sections);
    if (!activeLoc) return;

    let overLoc = findItemLocation(over.id, sections);
    if (!overLoc) {
      const overType = over.data.current?.type as string | undefined;
      if (overType === "container") {
        const parsed = parseChecklistContainerId(String(over.id));
        if (parsed) overLoc = parsed;
      } else if (overType === "subgroup") {
        const block = parseSubgroupBlockSortableId(String(over.id));
        if (block) {
          overLoc = { sectionIdx: block.sectionIdx, subgroupId: block.subgroupId };
        }
      } else if (overType === "section") {
        const idx = parseSectionSortableId(String(over.id));
        if (idx !== null) overLoc = { sectionIdx: idx, subgroupId: null };
      }
    }
    if (!overLoc) return;

    const sameContainer =
      activeLoc.sectionIdx === overLoc.sectionIdx &&
      activeLoc.subgroupId === overLoc.subgroupId;

    if (sameContainer) {
      const items = readContainerItems(
        sections,
        activeLoc.sectionIdx,
        activeLoc.subgroupId
      );
      const oldIndex = items.findIndex((it) => it.id === active.id);
      const newIndex = items.findIndex((it) => it.id === over.id);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const next = writeContainerItems(
        sections,
        activeLoc.sectionIdx,
        activeLoc.subgroupId,
        arrayMove(items, oldIndex, newIndex)
      );
      void persistSections(next);
      return;
    }

    const sourceItems = [
      ...readContainerItems(sections, activeLoc.sectionIdx, activeLoc.subgroupId),
    ];
    const destItems = [
      ...readContainerItems(sections, overLoc.sectionIdx, overLoc.subgroupId),
    ];
    const fromIndex = sourceItems.findIndex((it) => it.id === active.id);
    if (fromIndex < 0) return;
    const [moved] = sourceItems.splice(fromIndex, 1);
    if (!moved) return;

    const overIndex = destItems.findIndex((it) => it.id === over.id);
    if (overIndex >= 0) {
      destItems.splice(overIndex, 0, moved);
    } else {
      destItems.push(moved);
    }

    let next = writeContainerItems(
      sections,
      activeLoc.sectionIdx,
      activeLoc.subgroupId,
      sourceItems
    );
    next = writeContainerItems(
      next,
      overLoc.sectionIdx,
      overLoc.subgroupId,
      destItems
    );
    void persistSections(next);
  }

  function showAttribution(item: ApiChecklistItem): boolean {
    if (!item.checked || !item.checkedBy) return false;
    if (checklist && checklist.memberCount >= 2) return true;
    return item.checkedBy !== mySub;
  }

  function memberName(sub: string): string {
    if (me && sub === me.sub) return me.screenName;
    const member = members.find((candidate) => candidate.sub === sub);
    if (member?.screenName) return member.screenName;
    return liveNames[sub] ?? "Member";
  }

  function attributionLabel(item: ApiChecklistItem): {
    sub: string;
    screenName: string;
    firstName?: string;
    lastName?: string;
    color?: string;
    avatarKey?: string;
    googlePicture?: string;
    updatedAt?: string;
  } {
    const sub = item.checkedBy!;
    if (me && sub === me.sub) {
      return {
        sub: me.sub,
        screenName: memberName(sub),
        firstName: me.firstName,
        lastName: me.lastName,
        color: me.avatarColor,
        avatarKey: me.avatarKey,
        googlePicture: me.googlePicture,
        updatedAt: me.updatedAt,
      };
    }
    const member = members.find((candidate) => candidate.sub === sub);
    return {
      sub,
      screenName: item.screenName ?? memberName(sub),
      firstName: member?.firstName,
      lastName: member?.lastName,
      color: member?.avatarColor,
      avatarKey: member?.avatarKey,
      googlePicture: member?.googlePicture,
      updatedAt: member?.updatedAt,
    };
  }

  const isOwner = mySub !== "" && checklist?.ownerId === mySub;
  const canWrite =
    !!checklist &&
    (isOwner ||
      members.some((m) => m.sub === mySub && (m.role === "write" || m.role === "owner")) ||
      shareRole === "write");

  const queryLower = searchQuery.trim().toLowerCase();
  const filtering = queryLower.length > 0 || filter !== "all";
  const structureEditing = canWrite && pageMode === "edit";
  const compactRows = !structureEditing;
  const dndEnabled = structureEditing && !filtering && !persisting && !selection.isMultiSelectMode;

  useEffect(() => {
    if (!structureEditing) selection.exitMode();
  }, [structureEditing, selection.exitMode]);

  const pickerLists = useMemo(() => {
    const query = listPickerFilter.trim().toLowerCase();
    if (!query) return lists;
    return lists.filter(
      (list) =>
        list.name.toLowerCase().includes(query) ||
        list.description?.toLowerCase().includes(query)
    );
  }, [listPickerFilter, lists]);

  function onSortContainer(
    sectionIdx: number,
    subgroupId: string | null,
    dir: SortDir
  ) {
    if (!checklist || !dndEnabled) return;
    const sorted = sortItemsByText(
      readContainerItems(checklist.sections, sectionIdx, subgroupId),
      dir
    );
    void persistSections(
      writeContainerItems(checklist.sections, sectionIdx, subgroupId, sorted)
    );
  }

  const matchStats = useMemo(() => {
    if (!checklist) return { matchCount: 0, total: 0 };
    let matchCount = 0;
    let total = 0;
    for (const section of checklist.sections) {
      for (const item of section.items) {
        total += 1;
        if (itemMatchesFilter(item, queryLower, filter)) matchCount += 1;
      }
      for (const sg of section.subgroups ?? []) {
        for (const item of sg.items) {
          total += 1;
          if (itemMatchesFilter(item, queryLower, filter)) matchCount += 1;
        }
      }
    }
    return { matchCount, total };
  }, [checklist, queryLower, filter]);

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-muted">Loading…</div>
    );
  }

  if (notFound) return <ResourceNotFound kind="checklist" />;

  if (!checklist) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        {user ? (
          <Link
            to="/checklists"
            className="inline-flex min-h-6 items-center text-sm font-medium text-brand-700 no-underline hover:underline"
          >
            ← My Checklists
          </Link>
        ) : null}
        <div className="mt-4">
          <ErrorBanner message={error ?? "Checklist not found"} />
        </div>
      </div>
    );
  }

  const kind = normalizeListKind(checklist.kind);
  const displayTags = parseTags();
  const { checked, total } = countProgress(checklist);
  const pct = total === 0 ? 0 : Math.round((checked / total) * 100);
  const progressLabel = kind === "todo" ? "done" : "packed";
  const resetLabel = "Uncheck all";
  const sectionIds = checklist.sections.map((_, i) => sectionSortableId(i));
  // `renderItemRow` below is a hoisted function declaration, so it does not inherit
  // the `checklist` narrowing from the early-return checks above (TS limitation);
  // capture the non-nullable value here instead so nested closures can read it.
  const sections = checklist.sections;

  function renderItemRow(
    sectionIdx: number,
    item: ApiChecklistItem,
    subgroupId: string | null
  ) {
    if (!itemMatchesFilter(item, queryLower, filter)) return null;
    const attr = showAttribution(item) ? attributionLabel(item) : null;

    // Checked-off-by avatar sits on the row's right rail, immediately left of the
    // delete button - matching where it already appears outside edit mode - instead
    // of wrapping onto a second line under the item text.
    const attrBadge = attr ? (
      <HoverTapTip label={attr.screenName} className="mr-1">
        <Avatar
          sub={attr.sub}
          screenName={attr.screenName}
          firstName={attr.firstName}
          lastName={attr.lastName}
          color={attr.color}
          avatarKey={attr.avatarKey}
          googlePicture={attr.googlePicture}
          updatedAt={attr.updatedAt}
          size={16}
        />
      </HoverTapTip>
    ) : null;

    // Deliberate deviation from ui-ux-standard STANDARD.md Rule 4 (hover-reveal
    // selection): a checklist row has no leading icon or avatar for a checkbox to
    // cross-fade into, so a hover-revealed circle would have to reserve new width on
    // every row forever - exactly the cost Rule 4 exists to avoid. Entry points here
    // are the explicit "Select" toolbar toggle plus long-press under (pointer:coarse),
    // and the circle reuses the drag grip's own 32px slot so turning the mode on
    // shifts nothing. See docs/plans/.../CHECKLIST_EDIT_MODE_UI_PLAN.md D-2/D-3.
    const leading =
      structureEditing && selection.isMultiSelectMode ? (
        <span className="flex size-8 shrink-0 items-center justify-center">
          <SelectionCheckbox
            selected={selection.isSelected(item.id)}
            onChange={() => {
              selection.toggleOne(item.id);
              selection.setAnchor(item.id);
            }}
            label={`Select ${item.text}`}
          />
        </span>
      ) : null;

    function onRowClick(e: ReactMouseEvent) {
      if (!structureEditing) return;
      if (selection.consumeSuppressedClick()) return;
      if (e.shiftKey) {
        e.preventDefault();
        selection.selectMany(
          idsInRange(sections, selection.anchorId ?? item.id, item.id)
        );
        selection.setAnchor(item.id);
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        selection.toggleOne(item.id);
        selection.setAnchor(item.id);
        return;
      }
      if (selection.isMultiSelectMode) {
        selection.toggleOne(item.id);
        selection.setAnchor(item.id);
      }
    }
    const rowSelectionHandlers = {
      onClick: onRowClick,
      ...(structureEditing ? selection.longPressHandlers(item.id) : {}),
    };

    if (!dndEnabled && compactRows) {
      const rowCls = [
        "flex items-center gap-2.5 px-1 py-1.5",
        "min-h-9 [@media(pointer:coarse)]:min-h-11",
        canWrite || selection.isMultiSelectMode ? "cursor-pointer" : "",
      ]
        .filter(Boolean)
        .join(" ");
      const textCls = `min-w-0 flex-1 text-sm ${item.checked ? "text-muted line-through" : "text-ink"}`;
      const innards = (
        <>
          <input
            type="checkbox"
            className="min-h-6 min-w-6 shrink-0 rounded-lg border-line accent-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500/30"
            style={{ width: checkboxPx, height: checkboxPx }}
            checked={item.checked}
            disabled={!canWrite || busyItem === item.id || selection.isMultiSelectMode}
            onChange={() => void onToggle(sectionIdx, item, subgroupId)}
            aria-label={item.text}
          />
          <span className={textCls}>{item.text}</span>
          {attr ? (
            <HoverTapTip label={attr.screenName}>
              <Avatar
                sub={attr.sub}
                screenName={attr.screenName}
                firstName={attr.firstName}
                lastName={attr.lastName}
                color={attr.color}
                avatarKey={attr.avatarKey}
                googlePicture={attr.googlePicture}
                updatedAt={attr.updatedAt}
                size={16}
              />
            </HoverTapTip>
          ) : null}
        </>
      );
      return (
        <li key={item.id} className="group border-b border-line last:border-b-0">
          {selection.isMultiSelectMode || !canWrite ? (
            <div className={rowCls} {...rowSelectionHandlers}>
              {innards}
            </div>
          ) : (
            <label className={rowCls} {...rowSelectionHandlers}>
              {innards}
            </label>
          )}
        </li>
      );
    }

    const body = (
      <>
        <input
          type="checkbox"
          className={[
            "min-h-6 min-w-6 shrink-0 rounded-lg border-line accent-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500/30",
            selection.isMultiSelectMode ? "pointer-events-none" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ width: checkboxPx, height: checkboxPx }}
          checked={item.checked}
          disabled={!canWrite || busyItem === item.id || selection.isMultiSelectMode}
          onChange={() => void onToggle(sectionIdx, item, subgroupId)}
          aria-label={item.text}
        />
        <div className="min-w-0 flex-1">
          {editingItemId === item.id ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                className="min-w-0 flex-1 rounded-lg border border-brand-300 bg-white px-2 py-1 text-sm text-ink outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                value={editingItemText}
                maxLength={LIMITS.itemTextMax}
                aria-label="Edit item text"
                onChange={(e) => setEditingItemText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void saveItemEdit(sectionIdx, item.id, subgroupId);
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    cancelItemEdit();
                  }
                }}
              />
              <button
                type="button"
                className="rounded-lg p-1 text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                aria-label="Save item text"
                title="Save item text"
                onClick={() => void saveItemEdit(sectionIdx, item.id, subgroupId)}
              >
                <Check size={chevronPx} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                aria-label="Cancel item edit"
                title="Cancel"
                onClick={cancelItemEdit}
              >
                <X size={chevronPx} aria-hidden="true" />
              </button>
            </div>
          ) : structureEditing && !selection.isMultiSelectMode ? (
            <button
              type="button"
              className={`group flex max-w-full items-center gap-1 rounded-lg px-1 py-0.5 text-left text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 ${
                item.checked ? "text-muted line-through" : "text-ink"
              }`}
              aria-label={`Edit item: ${item.text}`}
              title="Edit item"
              onClick={() => startItemEdit(item)}
            >
              <span>{item.text}</span>
              <Pencil
                size={pencilPx}
                className="shrink-0 text-muted opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100"
                aria-hidden="true"
              />
            </button>
          ) : (
            <p className={`px-1 py-0.5 text-sm ${item.checked ? "text-muted line-through" : "text-ink"}`}>
              {item.text}
            </p>
          )}
        </div>
      </>
    );

    if (!dndEnabled) {
      return (
        <li
          key={item.id}
          className="group flex items-center gap-1.5 rounded-2xl border border-line bg-white px-2 py-2 sm:px-3"
          {...rowSelectionHandlers}
        >
          {leading}
          {body}
          {attrBadge}
          {structureEditing ? (
            <RemoveRowButton
              onClick={(e) => {
                e.stopPropagation();
                void onDeleteItem(sectionIdx, item.id, subgroupId);
              }}
            />
          ) : null}
        </li>
      );
    }

    return (
      <SortableRow
        key={item.id}
        id={item.id}
        data={{
          type: "item",
          containerId:
            subgroupId === null
              ? checklistUngroupedContainerId(sectionIdx)
              : checklistSubgroupContainerId(sectionIdx, subgroupId),
        }}
      >
        {({ dragHandleProps, isDragging, setNodeRef, style }) => (
          <li
            ref={setNodeRef}
            style={style}
            className={[
              "group flex items-center gap-1.5 rounded-2xl border border-line bg-white px-2 py-2 sm:px-3",
              isDragging ? "opacity-60 shadow-md" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            {...rowSelectionHandlers}
          >
            {leading ?? <DragHandle {...dragHandleProps} label="Drag to reorder item" />}
            {body}
            {attrBadge}
            {structureEditing ? (
              <RemoveRowButton
                onClick={(e) => {
                  e.stopPropagation();
                  void onDeleteItem(sectionIdx, item.id, subgroupId);
                }}
              />
            ) : null}
          </li>
        )}
      </SortableRow>
    );
  }

  /** Selects every item in `ids`, or deselects them if all are already selected. */
  function toggleSelectAll(ids: string[]) {
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selection.isSelected(id));
    if (allSelected) {
      selection.deselectMany(ids);
    } else {
      selection.selectMany(ids);
      selection.setAnchor(ids[ids.length - 1] ?? null);
    }
  }

  function sectionItemIds(section: ApiChecklistSection): string[] {
    return [
      ...section.items.map((it) => it.id),
      ...(section.subgroups ?? []).flatMap((sg) => sg.items.map((it) => it.id)),
    ];
  }

  function renderAddItem(sectionIdx: number, subgroupId: string | null) {
    if (!structureEditing) return null;
    const active =
      addTarget?.sectionIdx === sectionIdx && addTarget.subgroupId === subgroupId;
    const importBtn = (
      <Button
        type="button"
        variant="secondary"
        className="shrink-0"
        onClick={() => {
          setImportTarget({ sectionIdx, subgroupId });
          setImportOpen(true);
        }}
      >
        Import paste
      </Button>
    );
    if (active) {
      return (
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            className={inlineFieldCls}
            value={newItemText}
            maxLength={LIMITS.itemTextMax}
            placeholder="Ad-hoc item…"
            autoFocus
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void onAddItem();
              }
              if (e.key === "Escape") {
                setAddTarget(null);
                setNewItemText("");
              }
            }}
          />
          <Button
            type="button"
            className="shrink-0"
            disabled={adding}
            onClick={() => void onAddItem()}
          >
            {adding ? "…" : "Add"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="shrink-0"
            disabled={adding}
            onClick={() => {
              setAddTarget(null);
              setNewItemText("");
            }}
          >
            Cancel
          </Button>
          {importBtn}
        </div>
      );
    }
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="text-sm font-medium text-brand-700 hover:underline"
          onClick={() => {
            setAddTarget({ sectionIdx, subgroupId });
            setNewItemText("");
          }}
        >
          + Add item
        </button>
        {importBtn}
      </div>
    );
  }

  const sectionsContent = (
    <div className={compactRows ? "mt-4 space-y-4" : "mt-6 space-y-6"}>
      {checklist.sections.map((section, sectionIdx) => {
        const secProg = sectionProgress(section);
        const sectionCollapsed = !!collapse.sections[sectionIdx];
        const visibleUngrouped = section.items.filter((it) =>
          itemMatchesFilter(it, queryLower, filter)
        );
        const visibleSubgroups = (section.subgroups ?? []).filter((sg) =>
          sg.items.some((it) => itemMatchesFilter(it, queryLower, filter))
        );
        const sectionHasVisible =
          visibleUngrouped.length > 0 ||
          visibleSubgroups.length > 0 ||
          (!filtering &&
            (section.items.length === 0 && (section.subgroups ?? []).length === 0));

        if (filtering && !sectionHasVisible && secProg.total > 0) {
          // Hide sections with no matching items while filtering.
          const anyMatch =
            section.items.some((it) => itemMatchesFilter(it, queryLower, filter)) ||
            (section.subgroups ?? []).some((sg) =>
              sg.items.some((it) => itemMatchesFilter(it, queryLower, filter))
            );
          if (!anyMatch) return null;
        }

        const ungroupedIds = section.items.map((it) => it.id);
        const subgroupBlockIds = (section.subgroups ?? []).map((sg) =>
          subgroupBlockSortableId(sectionIdx, sg.id)
        );
        const ungroupedContainer = checklistUngroupedContainerId(sectionIdx);

        const sectionInner = (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {dndEnabled ? (
                <SortableRow
                  id={sectionSortableId(sectionIdx)}
                  data={{ type: "section" }}
                >
                  {({ dragHandleProps, isDragging, setNodeRef, style }) => (
                    <div
                      ref={setNodeRef}
                      style={style}
                      className={[
                        "flex min-w-0 flex-1 flex-wrap items-center gap-2",
                        isDragging ? "opacity-60" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <DragHandle {...dragHandleProps} label="Drag to reorder section" />
                      <button
                        type="button"
                        className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink"
                        aria-expanded={!sectionCollapsed}
                        aria-label={sectionCollapsed ? "Expand section" : "Collapse section"}
                        onClick={() =>
                          setCollapsePersisted((prev) => ({
                            ...prev,
                            sections: {
                              ...prev.sections,
                              [sectionIdx]: !prev.sections[sectionIdx],
                            },
                          }))
                        }
                      >
                        {sectionCollapsed ? (
                          <ChevronRight size={chevronPx} aria-hidden />
                        ) : (
                          <ChevronDown size={chevronPx} aria-hidden />
                        )}
                      </button>
                      {structureEditing ? (
                        <>
                          <input
                            className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-1 py-1.5 text-base font-semibold text-ink outline-none hover:border-line focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
                            defaultValue={section.name}
                            maxLength={120}
                            placeholder="Section name (optional)"
                            aria-label="Section name"
                            onBlur={(e) => {
                              const cleaned = e.target.value.trim().replace(/\s+/g, " ");
                              if (cleaned !== section.name) {
                                void onRenameSection(sectionIdx, e.target.value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                          />
                          {isLaterDuplicateSectionName(checklist.sections, sectionIdx) ? (
                            <span className="sr-only"> (section {sectionIdx + 1})</span>
                          ) : null}
                        </>
                      ) : (
                        <h2 className="min-w-0 flex-1 text-base font-semibold text-ink">
                          {displaySectionName(section.name)}
                          {isLaterDuplicateSectionName(checklist.sections, sectionIdx) ? (
                            <span className="sr-only"> (section {sectionIdx + 1})</span>
                          ) : null}
                        </h2>
                      )}
                      <span className="shrink-0 text-xs text-muted-sm">
                        {secProg.checked}/{secProg.total}
                      </span>
                      {structureEditing ? (
                        <RemoveRowButton
                          onClick={() => void onDeleteSection(sectionIdx)}
                        />
                      ) : null}
                    </div>
                  )}
                </SortableRow>
              ) : (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink"
                    aria-expanded={!sectionCollapsed}
                    aria-label={sectionCollapsed ? "Expand section" : "Collapse section"}
                    onClick={() =>
                      setCollapsePersisted((prev) => ({
                        ...prev,
                        sections: {
                          ...prev.sections,
                          [sectionIdx]: !prev.sections[sectionIdx],
                        },
                      }))
                    }
                  >
                    {sectionCollapsed ? (
                      <ChevronRight size={chevronPx} aria-hidden />
                    ) : (
                      <ChevronDown size={chevronPx} aria-hidden />
                    )}
                  </button>
                  <h2 className="min-w-0 flex-1 text-base font-semibold text-ink">
                    {displaySectionName(section.name)}
                    {isLaterDuplicateSectionName(checklist.sections, sectionIdx) ? (
                      <span className="sr-only"> (section {sectionIdx + 1})</span>
                    ) : null}
                  </h2>
                  <span className="shrink-0 text-xs text-muted-sm">
                    {secProg.checked}/{secProg.total}
                  </span>
                  {structureEditing ? (
                    <RemoveRowButton
                      onClick={() => void onDeleteSection(sectionIdx)}
                    />
                  ) : null}
                </div>
              )}
            </div>

            <SectionProgressMeter
              checked={secProg.checked}
              total={secProg.total}
              label={`Section ${section.name || sectionIdx + 1} progress`}
            />

            {!sectionCollapsed ? (
              <div className={compactRows ? "mt-1.5" : "mt-3"}>
                <div className="mb-2 flex flex-wrap items-center justify-end gap-1.5">
                    {structureEditing && selection.isMultiSelectMode ? (
                      <SelectionCheckbox
                        selected={
                          sectionItemIds(section).length > 0 &&
                          sectionItemIds(section).every((itemId) => selection.isSelected(itemId))
                        }
                        onChange={() => toggleSelectAll(sectionItemIds(section))}
                        label={`Select all items in ${section.name || "this section"}`}
                      />
                    ) : null}
                    <CopyItemsButton items={section.items} />
                    {structureEditing && !filtering ? (
                      <ItemSortButtons
                        items={section.items}
                        disabled={!dndEnabled}
                        onSort={(dir) => onSortContainer(sectionIdx, null, dir)}
                      />
                    ) : null}
                </div>
                <DroppableRegion
                  id={ungroupedContainer}
                  disabled={!dndEnabled}
                  className="min-h-[1.5rem]"
                >
                  {section.items.length === 0 &&
                  (section.subgroups ?? []).length === 0 ? (
                    <p className="text-sm text-muted">No items in this section.</p>
                  ) : visibleUngrouped.length === 0 && filtering ? null : section.items
                      .length === 0 ? (
                    <p className="text-sm text-muted">No ungrouped items.</p>
                  ) : (
                    <SortableList items={ungroupedIds} disabled={!dndEnabled}>
                      <ul className={compactRows ? "space-y-0" : "space-y-2"}>
                        {section.items.map((item) =>
                          renderItemRow(sectionIdx, item, null)
                        )}
                      </ul>
                    </SortableList>
                  )}
                </DroppableRegion>

                {renderAddItem(sectionIdx, null)}

                {(section.subgroups ?? []).length > 0 || structureEditing ? (
                  <div className="mt-4 flex flex-col gap-2">
                    <SortableList
                      items={subgroupBlockIds}
                      disabled={!dndEnabled}
                      className="flex flex-col gap-2"
                    >
                      {(section.subgroups ?? []).map((sg) => {
                        const sgProg = subgroupProgress(sg);
                        const sgCollapsed = !!collapse.subgroups[sg.id];
                        const visibleItems = sg.items.filter((it) =>
                          itemMatchesFilter(it, queryLower, filter)
                        );
                        if (filtering && visibleItems.length === 0) return null;
                        const containerId = checklistSubgroupContainerId(
                          sectionIdx,
                          sg.id
                        );
                        const blockId = subgroupBlockSortableId(sectionIdx, sg.id);
                        const itemIds = sg.items.map((it) => it.id);

                        if (!dndEnabled) {
                          return (
                            <div
                              key={sg.id}
                              className={
                                compactRows
                                  ? "mt-3 rounded-xl border border-line bg-white p-2 shadow-sm"
                                  : "rounded-xl border border-line bg-white p-3"
                              }
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink"
                                  aria-expanded={!sgCollapsed}
                                  aria-label={
                                    sgCollapsed
                                      ? "Expand subgroup"
                                      : "Collapse subgroup"
                                  }
                                  onClick={() =>
                                    setCollapsePersisted((prev) => ({
                                      ...prev,
                                      subgroups: {
                                        ...prev.subgroups,
                                        [sg.id]: !prev.subgroups[sg.id],
                                      },
                                    }))
                                  }
                                >
                                  {sgCollapsed ? (
                                    <ChevronRight size={chevronPx} aria-hidden />
                                  ) : (
                                    <ChevronDown size={chevronPx} aria-hidden />
                                  )}
                                </button>
                                {structureEditing ? (
                                  <input
                                    className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-1 py-1.5 text-sm font-semibold text-ink outline-none hover:border-line focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
                                    defaultValue={sg.name}
                                    maxLength={LIMITS.subgroupNameMax}
                                    aria-label="Subgroup name"
                                    onBlur={(e) => {
                                      if (e.target.value.trim() !== sg.name) {
                                        void onRenameSubgroup(
                                          sectionIdx,
                                          sg.id,
                                          e.target.value
                                        );
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        (e.target as HTMLInputElement).blur();
                                      }
                                    }}
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className="min-w-0 flex-1 rounded-lg px-1 py-0.5 text-left text-sm font-semibold text-ink hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                                    aria-expanded={!sgCollapsed}
                                    onClick={() =>
                                      setCollapsePersisted((prev) => ({
                                        ...prev,
                                        subgroups: {
                                          ...prev.subgroups,
                                          [sg.id]: !prev.subgroups[sg.id],
                                        },
                                      }))
                                    }
                                  >
                                    {sg.name}
                                  </button>
                                )}
                                <span className="shrink-0 text-xs text-muted-sm">
                                  {sgProg.checked}/{sgProg.total}
                                </span>
                                <SubgroupProgressMeter
                                  checked={sgProg.checked}
                                  total={sgProg.total}
                                  label={`${sg.name} subgroup progress`}
                                />
                                {structureEditing && selection.isMultiSelectMode ? (
                                  <SelectionCheckbox
                                    selected={
                                      sg.items.length > 0 &&
                                      sg.items.every((it) => selection.isSelected(it.id))
                                    }
                                    onChange={() =>
                                      toggleSelectAll(sg.items.map((it) => it.id))
                                    }
                                    label={`Select all items in ${sg.name || "this subgroup"}`}
                                  />
                                ) : null}
                                <CopyItemsButton items={sg.items} />
                                {structureEditing && !filtering ? (
                                  <ItemSortButtons
                                    items={sg.items}
                                    disabled={!dndEnabled}
                                    onSort={(dir) =>
                                      onSortContainer(sectionIdx, sg.id, dir)
                                    }
                                  />
                                ) : null}
                                {structureEditing ? (
                                  <RemoveRowButton
                                    onClick={() =>
                                      void onDeleteSubgroup(sectionIdx, sg.id)
                                    }
                                  />
                                ) : null}
                              </div>
                              {!sgCollapsed ? (
                                <div className="mt-2">
                                  {sg.items.length === 0 ? (
                                    <p className="text-sm text-muted">
                                      No items in this subgroup.
                                    </p>
                                  ) : (
                                    <ul className={compactRows ? "space-y-0" : "space-y-2"}>
                                      {sg.items.map((item) =>
                                        renderItemRow(sectionIdx, item, sg.id)
                                      )}
                                    </ul>
                                  )}
                                  {renderAddItem(sectionIdx, sg.id)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        return (
                          <SortableRow
                            key={sg.id}
                            id={blockId}
                            data={{ type: "subgroup", sectionIdx }}
                          >
                            {({
                              dragHandleProps,
                              isDragging,
                              setNodeRef,
                              style,
                            }) => (
                              <div
                                ref={setNodeRef}
                                style={style}
                                className={[
                                  "rounded-xl border border-line bg-white p-3",
                                  isDragging ? "opacity-60 shadow-md" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <DragHandle
                                    {...dragHandleProps}
                                    label="Drag to reorder subgroup"
                                  />
                                  <button
                                    type="button"
                                    className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink"
                                    aria-expanded={!sgCollapsed}
                                    aria-label={
                                      sgCollapsed
                                        ? "Expand subgroup"
                                        : "Collapse subgroup"
                                    }
                                    onClick={() =>
                                      setCollapsePersisted((prev) => ({
                                        ...prev,
                                        subgroups: {
                                          ...prev.subgroups,
                                          [sg.id]: !prev.subgroups[sg.id],
                                        },
                                      }))
                                    }
                                  >
                                    {sgCollapsed ? (
                                      <ChevronRight size={chevronPx} aria-hidden />
                                    ) : (
                                      <ChevronDown size={chevronPx} aria-hidden />
                                    )}
                                  </button>
                                  <input
                                  className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-1 py-1.5 text-sm font-semibold text-ink outline-none hover:border-line focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
                                    defaultValue={sg.name}
                                    maxLength={LIMITS.subgroupNameMax}
                                    aria-label="Subgroup name"
                                    onBlur={(e) => {
                                      if (e.target.value.trim() !== sg.name) {
                                        void onRenameSubgroup(
                                          sectionIdx,
                                          sg.id,
                                          e.target.value
                                        );
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        (e.target as HTMLInputElement).blur();
                                      }
                                    }}
                                  />
                                  <span className="shrink-0 text-xs text-muted-sm">
                                    {sgProg.checked}/{sgProg.total}
                                  </span>
                                  <SubgroupProgressMeter
                                    checked={sgProg.checked}
                                    total={sgProg.total}
                                    label={`${sg.name} subgroup progress`}
                                  />
                                  <CopyItemsButton items={sg.items} />
                                  {structureEditing && !filtering ? (
                                    <ItemSortButtons
                                      items={sg.items}
                                      disabled={!dndEnabled}
                                      onSort={(dir) =>
                                        onSortContainer(sectionIdx, sg.id, dir)
                                      }
                                    />
                                  ) : null}
                                  {structureEditing ? (
                                    <RemoveRowButton
                                      onClick={() =>
                                        void onDeleteSubgroup(sectionIdx, sg.id)
                                      }
                                    />
                                  ) : null}
                                </div>
                                {!sgCollapsed ? (
                                  <div className="mt-2">
                                    <DroppableRegion
                                      id={containerId}
                                      className="min-h-[1.5rem]"
                                    >
                                      {sg.items.length === 0 ? (
                                        <p className="text-sm text-muted">
                                          No items in this subgroup.
                                        </p>
                                      ) : (
                                        <SortableList items={itemIds}>
                                          <ul className={compactRows ? "space-y-0" : "space-y-2"}>
                                            {sg.items.map((item) =>
                                              renderItemRow(sectionIdx, item, sg.id)
                                            )}
                                          </ul>
                                        </SortableList>
                                      )}
                                    </DroppableRegion>
                                    {renderAddItem(sectionIdx, sg.id)}
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </SortableRow>
                        );
                      })}
                    </SortableList>

                    {structureEditing ? (
                      <div className="flex gap-2">
                        <input
                          className={inlineFieldCls}
                          value={newSubgroupName[sectionIdx] ?? ""}
                          maxLength={LIMITS.subgroupNameMax}
                          placeholder="New subgroup name…"
                          aria-label="New subgroup name"
                          onChange={(e) =>
                            setNewSubgroupName((prev) => ({
                              ...prev,
                              [sectionIdx]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void onAddSubgroup(sectionIdx);
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="shrink-0"
                          onClick={() => void onAddSubgroup(sectionIdx)}
                        >
                          Add subgroup
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        );

        return (
          <section key={`${section.name}-${sectionIdx}`}>{sectionInner}</section>
        );
      })}

      {structureEditing ? (
        <div className="rounded-2xl border border-dashed border-line p-4">
          {addingSection ? (
            <div className="flex flex-wrap gap-2">
              <input
                className={inlineFieldCls}
                value={newSectionName}
                maxLength={120}
                placeholder="Section name…"
                autoFocus
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void onAddSection();
                  }
                  if (e.key === "Escape") {
                    setAddingSection(false);
                    setNewSectionName("");
                  }
                }}
              />
              <Button type="button" size="sm" onClick={() => void onAddSection()}>
                Add section
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setAddingSection(false);
                  setNewSectionName("");
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {user ? (
                <button
                  type="button"
                  className="text-sm font-medium text-brand-700 hover:underline"
                  onClick={openListPicker}
                >
                  + Add list
                </button>
              ) : null}
              <button
                type="button"
                className="text-sm font-medium text-brand-700 hover:underline"
                onClick={() => {
                  setAddingSection(true);
                  setNewSectionName("");
                }}
              >
                + Add section
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={[
        "mx-auto w-full max-w-2xl px-4 py-8 sm:py-10",
        selection.isMultiSelectMode ? "pb-24" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {user ? (
            <GuardedLink
              to="/checklists"
              className="inline-flex min-h-6 items-center text-sm font-medium text-brand-700 no-underline hover:underline"
            >
              ← My Checklists
            </GuardedLink>
          ) : null}
          <h1 className="mt-2 text-2xl font-semibold text-ink">{checklist.name}</h1>
          {isChecklistArchived(checklist.archivedAt) ? (
            <p className="mt-2 rounded-md bg-stone-100 px-3 py-2 text-sm text-muted">
              Archived — hidden from the main My Checklists list. Anyone with access can still use it.
            </p>
          ) : null}
          {checklist.memberCount >= 2 ? (
            <p className="mt-1 text-xs text-muted-sm">
              Shared · {checklist.memberCount} members · live sync on
            </p>
          ) : null}
          {checklist.createdFromTemplateId ? (
            <p
              className="mt-1 text-xs text-muted-sm"
              title="Editing the source template or its lists won't change this checklist."
            >
              Snapshot from a template · started{" "}
              {formatInstant(checklist.createdAt, resolveDisplayZone())}
            </p>
          ) : null}
          <DescriptionHint show={!description.trim() && structureEditing} />
        </div>
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          {isOwner ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={savingTemplate}
              onClick={() => void onSaveAsTemplate()}
            >
              {savingTemplate ? "Saving…" : "Save as template"}
            </Button>
          ) : null}
          {canWrite ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={resetting || total === 0 || checked === 0}
              onClick={() => void onResetAll()}
            >
              {resetting ? "Unchecking…" : resetLabel}
            </Button>
          ) : null}
          <Button type="button" variant="secondary" size="icon" aria-label="Download PDF" title="Download PDF" onClick={() => setPdfOpen(true)}>
            <Download aria-hidden="true" size={20} />
          </Button>
          {isOwner ? <Button type="button" variant="secondary" size="icon" aria-label="Share checklist" title="Share checklist" onClick={() => setShareOpen(true)}><Share2 aria-hidden="true" size={20} /></Button> : null}
          {isOwner ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label={isChecklistArchived(checklist.archivedAt) ? "Unarchive checklist" : "Archive checklist"}
              title={isChecklistArchived(checklist.archivedAt) ? "Unarchive checklist" : "Archive checklist"}
              disabled={archiving}
              onClick={() => void onToggleArchive()}
            >
              <Archive aria-hidden="true" size={20} />
            </Button>
          ) : null}
          {isOwner ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="text-red-700"
              aria-label="Delete checklist"
              title="Delete checklist"
              disabled={deleting}
              onClick={() => void onDelete()}
            >
              <Trash2 aria-hidden="true" size={20} />
            </Button>
          ) : null}
        </div>
      </div>

      <ErrorBanner message={error} />
      {error?.includes("Someone else changed") ? <Button type="button" variant="secondary" size="sm" onClick={() => window.location.reload()}>Reload</Button> : null}
      {savedTemplate ? (
        <div
          role="status"
          className="rounded-lg border border-success/20 bg-success/5 px-3 py-2 text-sm text-success"
        >
          Saved as a template.{" "}
          <Link
            to={`/templates/${savedTemplate.id}`}
            className="font-medium underline underline-offset-2"
          >
            View template
          </Link>
        </div>
      ) : null}
      {savedList ? (
        <div
          role="status"
          className="rounded-lg border border-success/20 bg-success/5 px-3 py-2 text-sm text-success"
        >
          Saved as a list.{" "}
          <Link
            to={`/lists/${savedList.id}`}
            className="font-medium underline underline-offset-2"
          >
            View list
          </Link>
        </div>
      ) : null}

      {structureEditing && pendingDetails ? (
        <div className="mt-4">
          <DraftRestoreBar
            onRestore={() => {
              setDescription(pendingDetails.description);
              setTagsText(pendingDetails.tagsText);
              detailsDirtyRef.current = true;
              setPendingDetails(null);
            }}
            onDiscard={() => {
              clearDraft(detailsMirrorKey);
              setPendingDetails(null);
            }}
          />
        </div>
      ) : null}

      {structureEditing ? (
        <div className="mt-4 rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <label htmlFor="checklistDesc" className={labelCls}>
            Description
          </label>
          <textarea
            id="checklistDesc"
            className={`${fieldCls} min-h-[4.5rem] resize-y`}
            value={description}
            onChange={(e) => {
              detailsDirtyRef.current = true;
              setDescription(e.target.value);
            }}
            maxLength={500}
            placeholder="Optional notes about this checklist"
          />
          <div className="mt-3">
            <label htmlFor="checklistTags" className={labelCls}>
              Tags
            </label>
            <input
              id="checklistTags"
              className={fieldCls}
              value={tagsText}
              onChange={(e) => {
                detailsDirtyRef.current = true;
                setTagsText(e.target.value);
              }}
              placeholder="comma-separated, e.g. camping, kids"
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-3"
            disabled={savingDetails}
            onClick={() => void onSaveDetails()}
          >
            {savingDetails ? "Saving…" : "Save details"}
          </Button>
        </div>
      ) : description.trim() || displayTags.length ? (
        <div className="mt-4 rounded-2xl border border-line bg-surface p-4 shadow-sm">
          {description.trim() ? (
            <div>
              <p className={labelCls}>Description</p>
              <p className="mt-1 text-sm text-muted whitespace-pre-wrap">{description}</p>
            </div>
          ) : null}
          {displayTags.length ? (
            <div className={description.trim() ? "mt-3" : ""}>
              <p className={labelCls}>Tags</p>
              <div className="mt-1 flex flex-wrap gap-1.5" aria-label="Checklist tags">
                {displayTags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {structureEditing ? (
        <div className="mt-4">
          <p className={labelCls} id="checklistKindLabel">
            Kind
          </p>
          <div
            className="mt-1 flex flex-wrap gap-2"
            role="group"
            aria-labelledby="checklistKindLabel"
          >
            <button
              type="button"
              className={kindToggleCls(kind === "packing")}
              aria-pressed={kind === "packing"}
              onClick={() => void onKindChange("packing")}
            >
              Packing
            </button>
            <button
              type="button"
              className={kindToggleCls(kind === "todo")}
              aria-pressed={kind === "todo"}
              onClick={() => void onKindChange("todo")}
            >
              To-do
            </button>
          </div>
        </div>
      ) : null}

      <div
        className={[
          "top-0 z-10 mt-4 rounded-2xl border bg-surface/95 p-4 shadow-sm backdrop-blur-sm sm:sticky",
          celebrate || (total > 0 && checked >= total)
            ? "border-success/30"
            : "border-line",
          celebrate ? "checklist-complete-celebrate" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flex items-center justify-between gap-3 text-sm">
          <span
            className={[
              "font-semibold tabular-nums",
              total > 0 && checked >= total ? "text-success" : "text-ink",
            ].join(" ")}
          >
            {checked}/{total} {progressLabel}
          </span>
          <div className="flex items-center gap-2">
            <span
              className={
                total > 0 && checked >= total ? "font-medium text-success" : "text-muted"
              }
            >
              {pct}%
            </span>
            {structureEditing ? (
              <Button
                type="button"
                variant={selection.isMultiSelectMode ? "primary" : "secondary"}
                size="sm"
                aria-pressed={selection.isMultiSelectMode}
                aria-label={
                  selection.isMultiSelectMode ? "Exit multi-select mode" : "Enter multi-select mode"
                }
                title={
                  selection.isMultiSelectMode
                    ? "Exit multi-select"
                    : "Select multiple items for bulk actions"
                }
                onClick={() => {
                  if (selection.isMultiSelectMode) {
                    selection.exitMode();
                  } else {
                    cancelItemEdit();
                    selection.enterMode();
                  }
                }}
              >
                Select
              </Button>
            ) : null}
            {canWrite ? (
              <Button
                type="button"
                variant={structureEditing ? "primary" : "secondary"}
                size="sm"
                aria-label={structureEditing ? "Done editing" : "Edit checklist structure"}
                onClick={() => {
                  if (structureEditing) {
                    cancelItemEdit();
                    setAddTarget(null);
                    setAddingSection(false);
                    setPageMode("use");
                  } else {
                    setPageMode("edit");
                  }
                }}
              >
                {structureEditing ? "Done" : "Edit"}
              </Button>
            ) : null}
          </div>
        </div>
        <ProgressBar
          checked={checked}
          total={total}
          size="lg"
          className="mt-2"
          aria-label="Checklist progress"
        />
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search items…"
            aria-label="Search checklist items"
            className="max-w-none flex-1"
          />
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter items">
            {(
              [
                ["all", "All"],
                ["remaining", "Remaining"],
                ["done", "Done"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                className={filterChipCls(filter === mode)}
                aria-pressed={filter === mode}
                onClick={() => setFilter(mode)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {filtering ? (
          <p className="mt-2 text-xs text-muted-sm">
            Showing {matchStats.matchCount} of {matchStats.total}
            {structureEditing ? " · reordering paused while filtered" : ""}
            {structureEditing && matchStats.matchCount > 0 ? (
              <>
                {" · "}
                <button
                  type="button"
                  className="font-medium text-brand-700 hover:underline"
                  onClick={() => {
                    const ids = flattenItems(sections)
                      .filter((located) => itemMatchesFilter(located.item, queryLower, filter))
                      .map((located) => located.item.id);
                    selection.selectMany(ids);
                    selection.setAnchor(ids[ids.length - 1] ?? null);
                  }}
                >
                  Select all {matchStats.matchCount} matches
                </button>
              </>
            ) : null}
          </p>
        ) : null}
      </div>

      {dndEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={() => selection.exitMode()}
        >
          <SortableList items={sectionIds}>{sectionsContent}</SortableList>
        </DndContext>
      ) : (
        sectionsContent
      )}

      <SharingPanel
        resourceKind="checklist"
        resourceId={checklist.id}
        resourceName={checklist.name}
        isOwner={isOwner}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onChanged={() => {
          void loadChecklist({ silent: true });
          void loadMembers();
        }}
      />
      <GuestNamePrompt
        open={guestPromptOpen}
        onComplete={(guestName) => {
          setGuestName(guestName);
          setGuestPromptOpen(false);
          if (pendingToggle) {
            const next = pendingToggle;
            setPendingToggle(null);
            void onToggle(next.sectionIdx, next.item, next.subgroupId);
          }
        }}
      />
      <PdfExportDialog
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        docTitle={checklist.name}
        supportsProgress
        supportsAttribution={checklist.memberCount >= 2}
        shareResource={{ kind: "checklist", id: checklist.id, isOwner }}
        buildDoc={(options, qrUrl) =>
          checklistToPdfDoc(checklist, {
            includeAttribution: options.includeAttribution,
            memberName,
            url: qrUrl,
          })
        }
      />
      <ImportItemsModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportTarget(null);
        }}
        onConfirm={(items) => void onImportConfirm(items)}
        remaining={LIMITS.checklistItemsMax - totalItems(checklist)}
      />
      <Modal
        open={listPickerOpen}
        onClose={() => {
          if (!addingListId) setListPickerOpen(false);
        }}
        labelledBy="checklistListPickerTitle"
      >
        <h2 id="checklistListPickerTitle" className="text-lg font-semibold text-ink">
          Add a list
        </h2>
        <p className="mt-1 text-sm text-muted">
          The list is copied here as a new, unchecked section. Changes to the saved list will not
          change this checklist.
        </p>
        <input
          className={`${fieldCls} mt-4`}
          placeholder="Search lists…"
          value={listPickerFilter}
          onChange={(event) => setListPickerFilter(event.target.value)}
          autoFocus
        />
        {loadingLists ? (
          <p className="mt-4 text-sm text-muted">Loading lists…</p>
        ) : lists.length === 0 ? (
          <p className="mt-4 text-sm text-muted">
            No lists yet. <Link to="/lists/new" className="font-medium text-brand-700 hover:underline">Create a list</Link>{" "}
            first.
          </p>
        ) : pickerLists.length === 0 ? (
          <p className="mt-4 text-sm text-muted">No lists match your search.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {pickerLists.map((list) => {
              const count = list.itemCount;
              const exceedsRemaining = checklist && count > LIMITS.checklistItemsMax - totalItems(checklist);
              return (
                <li key={list.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-line bg-white px-3 py-3 text-left transition hover:border-brand-200 hover:bg-brand-50/40 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={Boolean(addingListId) || exceedsRemaining}
                    title={exceedsRemaining ? "This list would exceed the checklist item limit." : undefined}
                    onClick={() => void onAddListSection(list)}
                  >
                    <span>
                      <span className="block text-sm font-semibold text-ink">{list.name}</span>
                      <span className="mt-0.5 block text-xs text-muted-sm">
                        {count} item{count === 1 ? "" : "s"}
                        {normalizeListKind(list.kind) === "todo" ? " · To-do" : " · Packing"}
                      </span>
                    </span>
                    <span className="text-sm font-medium text-brand-700">
                      {addingListId === list.id ? "Adding…" : exceedsRemaining ? "Over limit" : "Add"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <Button
          type="button"
          variant="secondary"
          fullWidth
          className="mt-5"
          disabled={Boolean(addingListId)}
          onClick={() => setListPickerOpen(false)}
        >
          Cancel
        </Button>
      </Modal>
      <SelectionActionBar
        visible={selection.isMultiSelectMode}
        count={selection.selectedIds.size}
        canWrite={canWrite}
        canSaveList={Boolean(user)}
        busy={bulkBusy}
        selectedItems={collectSelected(sections, selection.selectedIds).map(
          (located) => located.item
        )}
        onMove={() => setMoveOpen(true)}
        onSaveAsList={() => setSaveOpen(true)}
        onCheck={(checkedNext) => void onBulkCheck(checkedNext)}
        onDelete={() => void onBulkDelete()}
        onExitMode={() => selection.exitMode()}
      />
      <MoveItemsModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        sections={sections}
        selectedIds={selection.selectedIds}
        busy={bulkBusy}
        onMove={(destination) => void onMoveSelected(destination)}
      />
      <SaveSelectionAsListModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        itemTexts={collectSelected(sections, selection.selectedIds).map(
          (located) => located.item.text
        )}
        defaultKind={checklist.kind}
        busy={bulkBusy}
        onSave={(input) => onSaveSelectionAsList(input)}
      />
    </div>
  );
}
